import {
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip
} from "@mui/material"
import {
  BASE_LAYER_LABEL,
  compareEngineVersions,
  fromLayerValue,
  toLayerValue,
  type PromptLayer
} from "@pages/main/scripts/prompts/prompt.models"

/**
 * The list's "every layer at once" selection. A sentinel rather than a separate type because a
 * layer is an engine version (`/^v\d+$/`), so no real layer can ever be called this.
 */
export const ALL_LAYERS = "__ALL__"

export type LayerFilter = PromptLayer

interface PromptLayerBarProps {
  /** Layers with at least one row, plus every engine version the AI service runs. */
  layers: PromptLayer[]
  value: LayerFilter
  onChange: (next: LayerFilter) => void
  countFor: (layer: LayerFilter) => number
}

/**
 * Which engine layer the list is showing — the first thing chosen on this page, above the search
 * box, because it decides what every other control on it means.
 *
 * A prompt name can exist once per layer, so without this the list shows the same name twice and
 * the only way to tell the rows apart is a chip. Picking the layer first makes "which version am
 * I about to edit" a deliberate answer rather than something to notice afterwards.
 */
export const PromptLayerBar = ({
  layers,
  value,
  onChange,
  countFor
}: PromptLayerBarProps) => {
  const ordered = [
    null,
    ...layers
      .filter((layer): layer is string => !!layer)
      .sort(compareEngineVersions)
  ]

  return (
    <Stack direction="row" sx={{ px: 1, pt: 1 }}>
      <ToggleButtonGroup
        exclusive
        size="small"
        fullWidth
        value={value === ALL_LAYERS ? ALL_LAYERS : toLayerValue(value)}
        onChange={(_e, next: string | null) => {
          if (!next) return
          onChange(next === ALL_LAYERS ? ALL_LAYERS : fromLayerValue(next))
        }}
      >
        {ordered.map((layer) => (
          <ToggleButton
            key={toLayerValue(layer)}
            value={toLayerValue(layer)}
            sx={{ fontSize: "0.7rem", fontWeight: 700, py: 0.4 }}
          >
            <Tooltip
              title={
                layer
                  ? `${layer}'s own rows — read only while ${layer} is answering`
                  : "The rows every engine version reads unless it has its own copy"
              }
            >
              <Box component="span">
                {layer ?? BASE_LAYER_LABEL}
                <Box
                  component="span"
                  sx={{ opacity: 0.55, ml: 0.5, fontWeight: 500 }}
                >
                  {countFor(layer)}
                </Box>
              </Box>
            </Tooltip>
          </ToggleButton>
        ))}
        <ToggleButton
          value={ALL_LAYERS}
          sx={{ fontSize: "0.7rem", fontWeight: 700, py: 0.4 }}
        >
          <Tooltip title="Every layer at once — names appear more than once">
            <Box component="span">
              ALL
              <Box
                component="span"
                sx={{ opacity: 0.55, ml: 0.5, fontWeight: 500 }}
              >
                {countFor(ALL_LAYERS)}
              </Box>
            </Box>
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>
    </Stack>
  )
}

/** The list badge — same colours as the editor banner, so a row reads the same in both places. */
export const layerChipStyles = (layer: PromptLayer) =>
  layer
    ? {
        bgcolor: "#FFF3E0",
        color: "#E65100",
        border: "1px solid #FFCC80"
      }
    : {
        bgcolor: "#E8F5E9",
        color: "#1B5E20",
        border: "1px solid #A5D6A7"
      }
